import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  done: "bg-forest text-white",
  running: "bg-amber-100 text-amber-800",
};

export function DigestCard() {
  const [runs, setRuns] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.adminDigestRuns().then(d => setRuns(d.runs || [])).catch(() => setRuns([]));
  useEffect(() => { load(); }, []);

  const dryRun = async () => {
    setBusy(true);
    try {
      const res = await api.adminDigestDryRun();
      setPreview(res);
      toast.success(`Dry run: ${res.sent} would receive the digest, ${res.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry run failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-14" data-testid="digest-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Mail className="w-5 h-5 text-terracotta" /> Weekly digest
      </h2>
      <p className="text-neutral-600 text-sm mt-1">
        Send history from the weekly volunteer digest cron. One real send per ISO week — repeats are blocked automatically.
      </p>
      <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Recent runs</div>
          <Button
            data-testid="digest-dry-run-btn"
            size="sm"
            variant="outline"
            className="rounded-full h-8 px-4 text-xs"
            onClick={dryRun}
            disabled={busy}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Computing…" : "Run dry-run now"}
          </Button>
        </div>
        {preview && (
          <div data-testid="digest-dry-run-result" className="mt-3 text-sm bg-forest/5 border border-forest/15 rounded-lg px-4 py-2.5 text-forest">
            Dry run for <b>{preview.week_iso}</b>: <b>{preview.sent}</b> volunteer{preview.sent === 1 ? "" : "s"} would receive it, {preview.skipped} skipped. No emails were sent.
          </div>
        )}
        <div className="mt-4" data-testid="digest-runs-list">
          {runs === null ? (
            <div className="text-sm text-neutral-400">Loading…</div>
          ) : runs.length === 0 ? (
            <div data-testid="digest-never-run" className="text-sm text-neutral-500">
              Never run — the first real send will appear here.
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {runs.map((r) => (
                <div key={r.week_iso} data-testid={`digest-run-${r.week_iso}`} className="py-2.5 flex items-center gap-3 flex-wrap text-sm">
                  <span className="font-mono text-forest font-semibold">{r.week_iso}</span>
                  <Badge className={`rounded-full border-none text-[11px] ${STATUS_STYLES[r.status] || "bg-neutral-200 text-neutral-700"}`}>
                    {r.status || "unknown"}
                  </Badge>
                  <span className="text-neutral-600">
                    {r.sent != null ? <>sent <b>{r.sent}</b> · skipped {r.skipped ?? 0}</> : "no send counts recorded"}
                  </span>
                  <span className="text-neutral-400 text-xs ml-auto">
                    {r.run_at ? new Date(r.run_at).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
